import { ApolloClient, InMemoryCache, createHttpLink, ApolloLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import * as Sentry from '@sentry/react';

const httpLink = createHttpLink({
	uri: import.meta.env.VITE_GRAPHQL_API_BASE_URL || 'localhost:4000/graphql'
});

const authLink = setContext((_, { headers }) => {
	const tokenString = sessionStorage.getItem('token');
	const token = tokenString ? JSON.parse(tokenString) : null;
	return {
		headers: {
			...headers,
			'x-csrf-token': 'WXwHOigPAj8zT3h1BCAcdQA0Jw5GeWMy0NrRglWX_8-B2vz9ZG_cwO9e',
			'Content-Type': 'application/json',
			authorization: token ? `Bearer ${token.state.token}` : ''
		}
	};
});

// Helper function to format the query string
const formatGraphQLQuery = (query: string): string => {
	return query
		.replace(/\\n/g, '\n') // Replace \n with actual newlines
		.replace(/\\t/g, '') // Remove \t
		.replace(/\s+/g, ' ') // Replace multiple spaces with single space
		.trim(); // Remove leading/trailing whitespace
};

// Helper function to format operation details for logging
const formatOperationDetails = (operation: any) => {
	return {
		variables: operation.variables,
		query: formatGraphQLQuery(operation.query.loc?.source.body)
	};
};

// Enhanced logging link
const loggingLink = new ApolloLink((operation, forward) => {
	const operationType =
		operation.query.definitions[0].kind === 'OperationDefinition'
			? operation.query.definitions[0].operation
			: 'unknown';

	console.log(`\n🚀 Starting ${operationType.toUpperCase()}: ${operation.operationName}`);
	console.log('📝 Operation details:');
	console.log(JSON.stringify(formatOperationDetails(operation), null, 2));

	return forward(operation).map((response) => {
		console.log(`\n✅ Response for ${operationType.toUpperCase()}: ${operation.operationName}`);
		console.log('📦 Response data:');
		console.log(JSON.stringify(response.data, null, 2));
		return response;
	});
});

// Error logging link
const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
	const operationType =
		operation.query.definitions[0].kind === 'OperationDefinition'
			? operation.query.definitions[0].operation
			: 'unknown';

	if (graphQLErrors) {
		graphQLErrors.forEach(({ message, locations, path }) => {
			const errorMessage =
				`\n❌ [GraphQL error]: ${operationType.toUpperCase()}: ${operation.operationName}` +
				`\nMessage: ${message}` +
				`\nLocation: ${JSON.stringify(locations)}` +
				`\nPath: ${path}`;

			console.error(errorMessage);
			Sentry.captureException(errorMessage);
		});
	}
	if (networkError) {
		const errorMessage = `\n🔴 [Network error in ${operationType.toUpperCase()} ${operation.operationName}]: ${networkError}`;
		console.error(errorMessage);
		Sentry.captureException(errorMessage);
	}
});

const client = new ApolloClient({
	link: ApolloLink.from([errorLink, loggingLink, authLink, httpLink]),
	cache: new InMemoryCache()
});

export default client;
